import {FC, useCallback, useEffect, useState} from "react";
import {
    PopupContainer,
    Container,
    BottomContainer,
    WarningBanner,
    ShiftDetailsCard,
    StatsSection,
    StatsSectionHeader,
    StatsGrid,
    StatCard,
    StatsNote,
    ConsequencesSection,
    ConsequenceItem,
    MembershipNotice,
    ConfirmDialog,
    ConfirmDialogHeader,
    ConfirmDialogBody,
    ConfirmDialogActions,
    PageActionButton,
    ModalSecondaryButton,
    ModalPrimaryButton,
} from "./CancelJobPage.styles";
import ModalTitleContainer from "components/Modal/components/ModalTitleContainer";
import styles from "./components.module.scss";
import useActionApi from "hooks/useActionApi";
import Api from "api";
import dayjs from "dayjs";
import {HygienistProps, HygienistScoreProps, JobProps} from "utils/types";
import {FormMaterialSelector} from "components/Selector";
import {FormMaterialInput} from "components/Inputs/MaterialInput";
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import joi from "joi";
import FixedLoader from "components/FixedLoader/FixedLoader";
import DefaultButton from "components/Buttons/DefaultButton";
import {useHistory} from "react-router";
import {
    Box,
    Typography,
    Button,
    MenuItem,
    IconButton,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    styled,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Help as HelpIcon,
    ErrorOutline as ErrorOutlineIcon,
    WarningAmberOutlined as WarningAmberOutlinedIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    TrendingDown as TrendingDownIcon,
    ExpandMore as ExpandMoreIcon,
    InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import Popup from "../../../components/Popup";
import TalentNavbar from "../../../components/TalentNavbar/TalentNavbar";

import figmaCancelModalHeaderIcon from "../../../images/figmaCancelModalHeaderIcon.svg";
import figmaCancelModalCloseIcon from "../../../images/figmaCancelModalCloseIcon.svg";
import figmaCancelModalIllustration from "../../../images/figmaCancelModalIllustration.svg";

const cancelThanksPopupDescription = 'We hope everything works out okay! Our support team has received the information about your cancellation and will be in contact about your account soon, hold tight.';

export const schema = joi
    .object({
        reason: joi.string().label('Reason').required(),
        details: joi.string().label('Details').required(),
    })
    .required();

const cancelReasons = [
    { value: 'illness', text: 'Illness' },
    { value: 'caregiver', text: 'Caregiver Related' },
    { value: 'transportation', text: 'Transportation Related' },
    { value: 'conflict', text: 'Scheduling Conflict' },
    { value: 'other', text: 'Other' },
];

export async function submitCancellationWithFollowUp({
    api,
    jobId,
    data,
}: {
    api: Pick<typeof Api, 'hygienistCancelJob' | 'updateCancelled'>;
    jobId: number;
    data: { reason: string; details: string };
}) {
    const cancelResult = await api.hygienistCancelJob(jobId, {
        reason: data.reason,
        details: data.details,
    });
    const hygienistCancelledId = cancelResult?.hygienistCancelledId;
    // Second PUT is email/compat only — reason/details are persisted on the initial POST (T2-6714)
    if (hygienistCancelledId != null) {
        try {
            await api.updateCancelled({ ...data, hygienistCancelledId });
        } catch (followUpErr) {
            console.warn('[CancelJobPage] updateCancelled follow-up failed', followUpErr);
        }
    }
    return cancelResult;
}

const PROVISIONAL_SHIFT_THRESHOLD = 5;

/** T2-6570: cancel confirmation shows FFS reliability as percentage only (no label). */
/** T2-6682 / T2-6259: <5 completed shifts → always "Building reliability history". */
export const formatReliabilityPercentileForCancel = (
    percentile?: number | null,
    _label?: string | null,
    completedShifts?: number | null,
) => {
    if (completedShifts != null && completedShifts < PROVISIONAL_SHIFT_THRESHOLD) {
        return 'Building reliability history';
    }
    if (percentile == null) {
        return 'Building reliability history';
    }
    return `${percentile}%`;
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
    },
}));

const CancelThanksPopup = ({onClose}: {onClose: () => void}) => (
    <PopupContainer>
        <ModalTitleContainer title={'Got it, thanks'}/>
        <div className={styles.modalDescription}>{cancelThanksPopupDescription}</div>
        <DefaultButton
            variant="contained"
            size="large"
            className={styles.buttonColour}
            onClick={onClose}
        >
            Submit
        </DefaultButton>
    </PopupContainer>
);

interface CancelJobPageProps {
    job: JobProps,
    hygienist: HygienistProps,
    cancellationStatsProps?: HygienistScoreProps
}

const CancelJobPage: FC = () => {
    const history = useHistory();
    const {
        job,
        hygienist,
        cancellationStatsProps,
    } = history.location.state as CancelJobPageProps;

    const [loading, setLoading] = useState(false);
    const [showAllStats, setShowAllStats] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [cancelThanksPopup, setCancelThanksPopup] = useState(false);
    const [openTooltip, setOpenTooltip] = useState(false);
    const [cancellationStats, setCancellationStats] = useState<HygienistScoreProps>({
        cancellationScore: 0,
        cancellations: 0,
        cancellations30Days: 0,
        cancellations90Days: 0,
        completed: 0,
        completed30Days: 0,
        completed90Days: 0,
        newCancellationScore: 0,
        newOverallScore: 0,
        newRecentScore: 0,
        newWeightedScore: 0,
        reliabilityPercentile: null,
        reliabilityLabel: null,
        newReliabilityPercentile: null,
        newReliabilityLabel: null,
        overallReliability: 0,
        recentReliability: 0,
        weightedReliability: 0,
        hygienist: {
            id: hygienist.id,
            firstName: hygienist.firstName,
        },
    });

    const { control, handleSubmit, watch } = useForm({
        defaultValues: { reason: '', details: '' } as { reason: string; details: string },
        resolver: joiResolver(schema) as any,
    });

    const [statsLoading, , getStats] = useActionApi({
        apiMethod: Api.calculateCancellationStats,
        onSuccess: (data: HygienistScoreProps) => {
            setCancellationStats(data);
        },
    });

    useEffect(() => {
        if (!cancellationStatsProps) {
            getStats({jobId: job.id});
            return;
        }
        setCancellationStats(cancellationStatsProps);
    }, [cancellationStatsProps, getStats, job.id]);

    const onCancel = async (data: any) => {
        setLoading(true);
        setShowConfirmModal(false);
        try {
            await submitCancellationWithFollowUp({ api: Api, jobId: job.id, data });
            setCancelThanksPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const goBack = useCallback(() => {
        history.goBack();
    }, [history]);

    const goHome = useCallback(() => {
        history.push({ pathname: 'hygienistHome', state: { reload: true } });
    }, [history]);

    const start = dayjs(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart);
    const gracePeriod = cancellationStats.newCancellationScore === cancellationStats.cancellationScore;
    const less24h = start.diff(dayjs(), 'hour') < 24;

    const formatReliabilityPercentile = formatReliabilityPercentileForCancel;

    const completedShifts = cancellationStats.completed;
    const stats = {
        reliabilityScore: {
            new: formatReliabilityPercentile(
                cancellationStats.newReliabilityPercentile,
                cancellationStats.newReliabilityLabel,
                completedShifts,
            ),
            current: formatReliabilityPercentile(
                cancellationStats.reliabilityPercentile,
                cancellationStats.reliabilityLabel,
                completedShifts,
            ),
        },
        cancellationsLast90Days: {
            new: cancellationStats.cancellations90Days + (gracePeriod ? 0 : 1),
            current: cancellationStats.cancellations90Days,
        },
        cancellationsLifetime: {
            new: cancellationStats.cancellations + (gracePeriod ? 0 : 1),
            current: cancellationStats.cancellations,
        },
    };

    const bannerVariant = gracePeriod ? 'grace' : less24h ? 'snc' : 'regular';
    const bannerIcon = gracePeriod
        ? <CheckCircleOutlineIcon className="banner-icon" />
        : less24h
            ? <WarningAmberOutlinedIcon className="banner-icon" />
            : <ErrorOutlineIcon className="banner-icon" />;
    const bannerTitle = gracePeriod ? 'Grace Period Cancellation' : "You're about to cancel a shift";
    const bannerSubtitle = gracePeriod
        ? 'You can cancel this shift without penalty. This cancellation is within your grace period (within 24 hours of booking and more than 30 days before the shift).'
        : less24h
            ? 'Cancelling this shift may impact your reliability rating and ability to book future shifts.'
            : 'Cancelling this shift will impact your reliability rating and could affect your ability to book future shifts. Please review the projected changes below before proceeding.';

    const formattedDate = dayjs(job.shifts[0].shiftDate).format('MMMM D, YYYY');
    const formattedTime = `${dayjs(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart).format('h:mm A')} - ${dayjs(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedEnd).format('h:mm A')}`;
    const renderProjectedChange = (
        newVal: number | string,
        currentVal: number | string,
    ) => {
        if (gracePeriod || newVal === currentVal) {
            return <span className="stat-card-projected stat-card-projected--neutral">No change</span>;
        }

        return (
            <span className="stat-card-projected">
                <TrendingDownIcon />
                <span className="stat-card-projected-value">{newVal}</span>
            </span>
        );
    };

    return (
        <>
            <Popup opened={cancelThanksPopup}>
                <CancelThanksPopup onClose={() => {
                    setCancelThanksPopup(false);
                    goHome();
                }} />
            </Popup>

            <ConfirmDialog
                open={showConfirmModal}
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick') {
                        setShowConfirmModal(false);
                    }
                }}
                disableEscapeKeyDown
            >
                <ConfirmDialogHeader>
                    <div className="dialog-header-left">
                        <img src={figmaCancelModalHeaderIcon} alt="" width={18} height={18} />
                        Cancellation
                    </div>
                    <IconButton
                        size="small"
                        onClick={() => setShowConfirmModal(false)}
                        sx={{ color: '#697586' }}
                    >
                        <img src={figmaCancelModalCloseIcon} alt="Close" width={18} height={18} />
                    </IconButton>
                </ConfirmDialogHeader>
                <ConfirmDialogBody>
                    <img src={figmaCancelModalIllustration} alt="" className="dialog-icon" />
                    <Typography className="dialog-title">Cancel this shift?</Typography>
                    <Typography className="dialog-desc">
                        Are you sure you want to cancel this shift? This may affect your reliability rating and future shift bookings.
                    </Typography>
                </ConfirmDialogBody>
                <ConfirmDialogActions>
                    <ModalSecondaryButton variant="outlined" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </ModalSecondaryButton>
                    <ModalPrimaryButton variant="contained" onClick={handleSubmit(onCancel)}>
                        Confirm
                    </ModalPrimaryButton>
                </ConfirmDialogActions>
            </ConfirmDialog>

            <div className={styles.pageContainer}>
                <TalentNavbar
                    firstName={hygienist?.firstName}
                    lastName={hygienist?.lastName}
                    photoUrl={hygienist?.photoUrl || undefined}
                    isHygOrAsst={hygienist?.type === 0 || hygienist?.type === 1}
                    instabookActivated={hygienist?.InstabookActivated === 1}
                    isTestAccount={(hygienist as any)?.isTest === 1}
                    showAddShiftButton={true}
                    showJobBoardButton={true}
                />

                <main className={styles.mainContent}>
                    <div className={styles.subheader}>
                        <button className={styles.backButton} onClick={goBack} aria-label="Back">
                            <ArrowBackIcon />
                        </button>
                    </div>

                    <Container>
                        <WarningBanner $variant={bannerVariant}>
                            {bannerIcon}
                            <Box>
                                <Typography fontWeight={700} fontSize="0.95rem" color="text.primary">
                                    {bannerTitle}
                                </Typography>
                                <Typography fontSize="0.8rem" color="text.secondary" mt={0.25}>
                                    {bannerSubtitle}
                                </Typography>
                            </Box>
                        </WarningBanner>

                        <Typography fontWeight="bold" color="text.primary" mb={0.75} fontSize="0.95rem">
                            Shift Details
                        </Typography>
                        <ShiftDetailsCard>
                            <div className="detail-row">
                                <span className="detail-label">Date</span>
                                <span className="detail-value">{formattedDate}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Time</span>
                                <span className="detail-value">{formattedTime}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Office</span>
                                <span className="detail-value">{job.location.practiceName}</span>
                            </div>
                        </ShiftDetailsCard>

                        <StatsSection>
                            <StatsSectionHeader>
                                <Typography fontWeight="bold" fontSize="0.95rem" color="text.primary" display="flex" alignItems="center">
                                    Your Reliability Statistics
                                    <HtmlTooltip
                                        open={openTooltip}
                                        onClick={() => setOpenTooltip(!openTooltip)}
                                        title={
                                            <>
                                                {`Your Reliability Score shows your FFS reliability percentile compared with other active Talent. Avoiding shift cancellations keeps your percentile strong, highlights your dependability to dental offices, and leads to more booking opportunities at higher rates. `}
                                                <a
                                                    href="https://support.tempstars.com/hc/en-us/articles/36272510254867-Reliabilty-Score"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="external"
                                                >
                                                    Learn more...
                                                </a>
                                            </>
                                        }
                                    >
                                        <IconButton size="small">
                                            <HelpIcon sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </HtmlTooltip>
                                </Typography>
                                <Button
                                    onClick={() => setShowAllStats(!showAllStats)}
                                    sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}
                                >
                                    {showAllStats ? 'Show less' : 'Show more'}
                                    <ExpandMoreIcon
                                        sx={{
                                            fontSize: '1.1rem',
                                            ml: 0.25,
                                            transform: showAllStats ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s',
                                        }}
                                    />
                                </Button>
                            </StatsSectionHeader>

                            <StatsGrid>
                                <StatCard>
                                    <span className="stat-card-label">Reliability Score</span>
                                    <div className="stat-card-row">
                                        <span className="stat-card-current">
                                            Currently: <strong>{stats.reliabilityScore.current}</strong>
                                        </span>
                                        {renderProjectedChange(stats.reliabilityScore.new, stats.reliabilityScore.current)}
                                    </div>
                                </StatCard>
                                <StatCard>
                                    <span className="stat-card-label">Cancellations – Last 90 Days</span>
                                    <div className="stat-card-row">
                                        <span className="stat-card-current">
                                            Currently: <strong>{stats.cancellationsLast90Days.current}</strong>
                                        </span>
                                        {renderProjectedChange(stats.cancellationsLast90Days.new, stats.cancellationsLast90Days.current)}
                                    </div>
                                </StatCard>
                                {showAllStats && (
                                    <>
                                        <StatCard>
                                            <span className="stat-card-label">Cancellations – Lifetime</span>
                                            <div className="stat-card-row">
                                                <span className="stat-card-current">
                                                    Currently: <strong>{stats.cancellationsLifetime.current}</strong>
                                                </span>
                                                {renderProjectedChange(stats.cancellationsLifetime.new, stats.cancellationsLifetime.current)}
                                            </div>
                                        </StatCard>
                                    </>
                                )}
                            </StatsGrid>

                            <StatsNote>
                                <Typography component="span" sx={{ fontWeight: 700, color: '#0B4598', fontSize: '0.8rem', flexShrink: 0 }}>
                                    Note:
                                </Typography>
                                <span>These projected changes will be reflected in your profile after confirming this cancellation.</span>
                            </StatsNote>
                        </StatsSection>

                        {!gracePeriod && (
                            <ConsequencesSection>
                                <Typography fontWeight="bold" fontSize="0.95rem" color="text.primary" mb={0.5}>
                                    Consequences of Cancellation
                                </Typography>
                                <ConsequenceItem>
                                    <div className="consequence-number">1</div>
                                    <div className="consequence-content">
                                        <span className="consequence-title">Late cancellation penalty</span>
                                        <span className="consequence-desc">Cancelling within 24 hours of the shift start may result in a reliability score deduction.</span>
                                    </div>
                                </ConsequenceItem>
                                <ConsequenceItem>
                                    <div className="consequence-number">2</div>
                                    <div className="consequence-content">
                                        <span className="consequence-title">Reliability score impact</span>
                                        <span className="consequence-desc">Your reliability score may decrease, which can affect your priority for future shift assignments.</span>
                                    </div>
                                </ConsequenceItem>
                                <ConsequenceItem>
                                    <div className="consequence-number">3</div>
                                    <div className="consequence-content">
                                        <span className="consequence-title">Temporary booking restrictions</span>
                                        <span className="consequence-desc">Depending on your membership tier, you may face temporary restrictions on booking new shifts.</span>
                                    </div>
                                </ConsequenceItem>
                            </ConsequencesSection>
                        )}

                        {!gracePeriod && (
                            <MembershipNotice>
                                <div className="notice-header">
                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#0B4598' }} />
                                    Membership Status Notice
                                </div>
                                <ul>
                                    <li>
                                        <Typography fontSize="0.85rem">
                                            Active and Pro members may receive temporary booking restrictions after cancelling a shift.
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography fontSize="0.85rem">
                                            Elite members will not be blocked from booking shifts. If you need assistance, please <a href="mailto:help@tempstars.com">Contact Support</a>.
                                        </Typography>
                                    </li>
                                </ul>
                            </MembershipNotice>
                        )}

                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                                fontWeight={600}
                                fontSize="0.75rem"
                                color="text.secondary"
                                letterSpacing="0.08em"
                                mb={0.25}
                            >
                                CANCELATION REASON
                            </Typography>
                            <Typography fontWeight={600} fontSize="0.9rem" color="text.primary" mb={0.25}>
                                Reason for Cancellation
                            </Typography>
                            <FormMaterialSelector
                                control={control}
                                className={styles.styledSelector}
                                name={"reason"}
                                label="Select a reason"
                            >
                                {cancelReasons.map((reason, index) => (
                                    <MenuItem value={reason.value} key={reason.value + index}>{reason.text}</MenuItem>
                                ))}
                            </FormMaterialSelector>
                            <Typography fontWeight={600} fontSize="0.9rem" color="text.primary" mt={0.75} mb={0.25}>
                                Additional details
                            </Typography>
                            <FormMaterialInput
                                control={control}
                                name={'details'}
                                label={"Type here..."}
                                multiline
                                rows={4}
                            />
                        </Box>

                        <BottomContainer>
                            <PageActionButton
                                onClick={() => setShowConfirmModal(true)}
                                variant="contained"
                                color="error"
                                disabled={!watch("reason") || !watch("details")}
                            >
                                Cancel Shift
                            </PageActionButton>
                        </BottomContainer>
                    </Container>
                </main>
            </div>

            <FixedLoader loading={loading || statsLoading}/>
        </>
    );
};

export default CancelJobPage;
